import React from 'react';
import { motion } from 'framer-motion';
import { responsiveImg } from '../../lib/imageUrl';
import { useSlotImage } from '../../lib/useSlotImage';
import { useText } from '../../lib/siteContent';
import { UniverseLinks } from './UniverseLinks';

const MAIN_FALLBACK = 'https://images.unsplash.com/photo-1606660023296-81d67734170a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';

const SERVICES = [
  { n: '01', title: 'Dîners privés', desc: 'Tables signature et expériences culinaires.' },
  { n: '02', title: 'Galas & réceptions', desc: 'Accueillir, remercier et célébrer.' },
  { n: '03', title: 'Événements d’entreprise', desc: 'Rencontres, remises de prix et soirées.' },
  { n: '04', title: 'Événements de marque', desc: 'Lancements, presse et activations.' },
  { n: '05', title: 'Production', desc: 'Scénographie, lumière et coordination technique.' },
  { n: '06', title: 'Coordination', desc: 'Déroulement, prestataires et invités.' },
];

const ease = [0.22, 1, 0.36, 1];

const ServiceItem = ({ s, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.8, delay: (i % 2) * 0.1, ease }}
    className="border-t border-[#111111]/15 pt-7"
    data-testid={`reception-service-${s.n}`}
  >
    <span className="block text-[11px] tracking-[0.3em] text-[#6B6B6B]">{s.n}</span>
    <h3 className="mt-4 font-serif font-light text-[#111111] text-2xl md:text-[1.7rem] leading-[1.2]">{s.title}</h3>
    <p className="mt-3 text-sm leading-[1.85] font-light text-[#333333]">{s.desc}</p>
  </motion.div>
);

export const ReceptionsContent = () => {
  const image = useSlotImage('receptions-main', MAIN_FALLBACK);
  const t = useText();
  return (
    <main data-testid="universe-page-receptions-corporate">
      <section className="container-custom pt-16 md:pt-24 lg:pt-32 grid grid-cols-1 lg:grid-cols-[5fr_7fr] lg:gap-x-16 xl:gap-x-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, ease }} className="lg:col-start-2 lg:row-start-1 lg:pt-6">
          <span className="block text-[11px] uppercase tracking-[0.3em] text-[#6B6B6B] mb-8 md:mb-10 leading-relaxed" data-testid="universe-label">
            {t('receptions.label')}
          </span>
          <h1 className="font-serif font-light text-[#111111] text-5xl sm:text-6xl xl:text-7xl leading-[1.04]" data-testid="universe-title">
            <span className="whitespace-pre-line">{t('receptions.headline')}</span>
          </h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.4 }}
          className="mt-12 lg:mt-0 lg:col-start-1 lg:row-start-1 lg:row-span-3 lg:sticky lg:top-32 self-start w-full aspect-[4/5] md:aspect-[4/3] lg:aspect-[3/4] overflow-hidden bg-[#F5F5F5]"
        >
          <img {...responsiveImg(image, '(max-width: 768px) 100vw, 50vw', { eager: true })} alt="Dîner privé — image d'illustration" className="w-full h-full object-cover" data-testid="universe-hero-image" />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.1, delay: 0.2 }}
          className="mt-12 lg:mt-10 lg:col-start-2 lg:row-start-2 text-sm md:text-base leading-[1.9] font-light text-[#333333] max-w-xl"
          data-testid="universe-intro"
        >
          {t('receptions.intro')}
        </motion.p>

        <div className="mt-16 lg:mt-20 lg:col-start-2 lg:row-start-3 grid grid-cols-1 md:grid-cols-2 gap-x-12 xl:gap-x-16 gap-y-12 lg:gap-y-14" data-testid="reception-services">
          {SERVICES.map((s, i) => ({ ...s, title: t(`receptions.s${i + 1}.title`), desc: t(`receptions.s${i + 1}.desc`) })).map((s, i) => <ServiceItem key={s.n} s={s} i={i} />)}
        </div>
      </section>

      <UniverseLinks />
    </main>
  );
};
